import React, { useState, useEffect, Fragment, useContext } from "react";
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import { FileUploader } from "react-drag-drop-files";
import { AuthContext } from "../../../Context/AuthContext";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { Authorization } from "../../../Context/Authorization";

const IndividualLoan = () => {
  const [vfcNames, setVfcNames] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userId } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    member: "",
    loanAmount: "",
    loanType: "",
    installment: "",
    purposeOfLoan: "",
    cashOrBank: "",
    status: "AL",
    groupType: "Individual",
    uploadDocument: "",
  });
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState("");
  const [loanTypes, setLoanTypes] = useState([]);
  const [isOtherAmount, setIsOtherAmount] = useState(false);
  const [otherAmount, setOtherAmount] = useState("");
  const Api = process.env.REACT_APP_API;
  const [selectedLoanType, setSelectedLoanType] = useState("");

  useEffect(() => {
    setLoading(true);

    const fetchVfcNames = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/vfcs`, config);
        const data = response.data.vfcs;

        if (Array.isArray(data)) {
          setVfcNames(data);
        } else {
          console.error("API did not return an array", data);
        }
      } catch (error) {
        console.log("Error fetching Data", error);
      }
      setLoading(false);
    };

    const fetchLoanTypes = async () => {
      try {
        const config = Authorization();
        const response = await fetch(`${Api}/api/loan-types`, config);
        const data = await response.json();

        if (Array.isArray(data.loanTypes)) {
          setLoanTypes(data.loanTypes);

          setFormData((prev) => ({
            ...prev,
            loanType: selectedLoanType._id,
          }));
        } else {
          console.error(
            "Expected 'loanTypes' to be an array but got:",
            data.loanTypes
          );
        }
        setLoanTypes(data.loanTypes);
      } catch (error) {
        console.log("Error fetching loan types", error);
      }
      setLoading(false);
    };

    const fetchInstallments = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/installments`, config);
        setInstallments(response.data);
      } catch (err) {
        console.error("Error fetching data", err);
      }
      setLoading(false);
    };

    fetchInstallments();

    fetchVfcNames();
    fetchLoanTypes();
  }, []);

  const handleVfcChange = async (event) => {
    const vfcId = event.target.value;
    setSelectedVfc(vfcId);
    setLoading(true);
    try {
      const config = Authorization();
      const response = await fetch(
        `${Api}/api/members/getMembersByVFC/${vfcId}`,
        config
      );
      const data = await response.json();
      setMembers(data.members);
    } catch (error) {
      console.log("Error fetching members", error);
    }
    setLoading(false);
  };

  const handleInputChange = (event) => {
    const { name, type, value, files } = event.target;
    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0], // Ensure uploadDocument stores the file
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleLoanAmountChange = (event) => {
    const value = event.target.value;
    if (value === "Other") {
      setIsOtherAmount(true);
    } else {
      setIsOtherAmount(false);
      setFormData({ ...formData, loanAmount: value });
    }
  };

  const handleOtherAmountChange = (event) => {
    setOtherAmount(event.target.value);
    setFormData({ ...formData, loanAmount: event.target.value });
  };
  const handleFileUpload = (file) => {
    setFormData((prev) => ({
      ...prev,
      uploadDocument: file, // Store file in state
    }));
  };

  const validateForm = () => {
    const { member, loanAmount, loanType, purposeOfLoan, cashOrBank } =
      formData;
    const newErrors = {};
    if (!selectedVfc) newErrors.vfc = "VFC is required";
    if (!member) newErrors.member = "Member is required";
    if (!loanAmount) newErrors.loanAmount = "Loan amount is required";
    if (!loanType) newErrors.loanType = "Loan type is required";
    if (!purposeOfLoan) newErrors.purposeOfLoan = "Purpose of loan is required";
    if (!cashOrBank) newErrors.purposeOfLoan = "Cash Or Bank is required";
    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!formData.installment) {
      console.error("Installment is required but missing.");
      return;
    }

    // Create FormData
    const formDataToSend = new FormData();
    if (formData.uploadDocument) {
      formDataToSend.append("uploadDocument", formData.uploadDocument);
    }
    formDataToSend.append("vfcId", selectedVfc);
    formDataToSend.append("loanType", formData.loanType);
    formDataToSend.append("installment", formData.installment);
    formDataToSend.append("groupType", formData.groupType);

    formDataToSend.append(
      "amount",
      isOtherAmount ? otherAmount : formData.loanAmount
    );
    formDataToSend.append("shgId", selectedVfc);
    formDataToSend.append("cashOrBank", formData.cashOrBank);
    formDataToSend.append("status", "AL");

    // Append subLoan as JSON
    const subLoans = [
      {
        memberId: formData.member,
        subLoanId: "individualSubLoan",
        disbursedAmount: isOtherAmount ? otherAmount : formData.loanAmount,
      },
    ];
    formDataToSend.append("subLoans", JSON.stringify(subLoans)); // Convert to JSON string
    for (let pair of formDataToSend.entries()) {
      console.log(pair[0], pair[1]);
    }

    try {
      const config = Authorization(); // Ensure this returns the token correctly
      const response = await fetch(`${Api}/api/loans`, {
        method: "POST",
        headers: {
          ...config.headers,
        },
        body: formDataToSend,
      });
      setSubmitMessage("Loan application submitted successfully!");
      await logActivity(
        userId,
        "New Individual Loan Created",
        "Individual Loan Page",
        Api
      );
      if (!response.ok) {
        throw new Error("Failed to submit loan application");
      }
      // Reset form
      setFormData({
        member: "",
        loanAmount: "",
        loanType: loanTypes.length > 0 ? loanTypes[0]._id : "",
        purposeOfLoan: "",
        installment: "",
        cashOrBank: "",
        status: "AL",
      });
      setSubmitMessage("");
      setSelectedVfc("");
      setMembers([]);
      setIsOtherAmount(false);
      setOtherAmount("");
      setErrors({});
    } catch (error) {
      console.error("Error submitting form", error);
      setSubmitMessage("Error submitting loan application. Please try again.");
    }
  };

  return (
    <div>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title"> Individual Loan </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Individual Loan
            </li>
          </ol>
        </nav>
      </div>

      <Fragment>
        <div className="col-md-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <p className="card-description">Individual Loan Form</p>
              <Form onSubmit={handleSubmit}>
                {submitMessage && <Alert variant="info">{submitMessage}</Alert>}

                <Row>
                  <Col xs={6} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>VFC Name</Form.Label>
                      <Form.Control
                        as="select"
                        value={selectedVfc}
                        onChange={handleVfcChange}
                        isInvalid={!!errors.vfc}
                      >
                        <option value="">Select VFC</option>
                        {vfcNames.map((vfc) => (
                          <option key={vfc._id} value={vfc._id}>
                            {vfc.vfcName}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.vfc}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Member</Form.Label>
                      <Form.Control
                        as="select"
                        name="member"
                        value={formData.member}
                        onChange={handleInputChange}
                        isInvalid={!!errors.member}
                      >
                        <option value="">Select Member</option>
                        {members.map((member) => (
                          <option key={member._id} value={member._id}>
                            {member.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.member}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Loan Amount</Form.Label>
                      <Form.Control
                        as="select"
                        name="loanAmount"
                        value={isOtherAmount ? "Other" : formData.loanAmount}
                        onChange={handleLoanAmountChange}
                        isInvalid={!!errors.loanAmount}
                      >
                        <option value="">Select Loan Amount</option>
                        <option value="5000">5,000</option>
                        <option value="10000">10,000</option>
                        <option value="15000">15,000</option>
                        <option value="Other">Other</option>
                      </Form.Control>

                      {isOtherAmount && (
                        <Form.Control
                          type="number"
                          placeholder="Enter Loan Amount"
                          value={otherAmount}
                          onChange={handleOtherAmountChange}
                        />
                      )}
                      <Form.Control.Feedback type="invalid">
                        {errors.loanAmount}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Installment</Form.Label>
                      <Form.Control
                        as="select"
                        name="installment"
                        value={formData.installment || ""}
                        onChange={handleInputChange}
                        isInvalid={!!errors.installment}
                      >
                        <option value="">Select Installment</option>
                        {installments.map((installment) => (
                          <option key={installment._id} value={installment._id}>
                            {installment.installmentNumber}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.installment}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Loan Type</Form.Label>
                      <Form.Control
                        as="select"
                        value={formData.loanType}
                        onChange={(e) =>
                          setFormData({ ...formData, loanType: e.target.value })
                        }
                      >
                        <option value="">Select Loan Type</option>
                        {loanTypes.map((loan) => (
                          <option key={loan._id} value={loan._id}>
                            {loan.loanName}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Cash Or Bank</Form.Label>
                      <Form.Control
                        as="select"
                        name="cashOrBank"
                        value={formData.cashOrBank}
                        onChange={handleInputChange}
                        isInvalid={!!errors.cashOrBank}
                      >
                        <option value="">Select</option>
                        <option value="cash">Cash</option>
                        <option value="bank">Bank</option>
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.purposeOfLoan}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Purpose Of Loan</Form.Label>
                      <Form.Control
                        type="text"
                        name="purposeOfLoan"
                        placeholder="Purpose Of Loan"
                        value={formData.purposeOfLoan}
                        onChange={handleInputChange}
                        isInvalid={!!errors.purposeOfLoan}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.purposeOfLoan}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  {/* Right column: File Upload and Criteria */}
                  <Col xs={6} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>Upload Documents</Form.Label>
                      <FileUploader
                        type="file"
                        multiple={false}
                        name="uploadDocument"
                        handleChange={(file) => handleFileUpload(file)}
                        types={["jpg", "png", "gif", "pdf"]}
                        onChange={handleInputChange}
                      />
                    </Form.Group>

                    <strong>Loan Issue Criteria</strong>
                    <ul>
                      <li>Criteria 1</li>
                      <li>Criteria 2</li>
                      <li>Criteria 3</li>
                      <li>Criteria 4</li>
                      <li>Criteria 5</li>
                    </ul>
                  </Col>
                </Row>

                <button
                  type="submit"
                  className="btn btn-outline-primary btn-icon-text mt-3"
                >
                  <i className="mdi mdi-file-check btn-icon-prepend"></i>
                  Submit
                </button>
              </Form>
            </div>
          </div>
        </div>
      </Fragment>
    </div>
  );
};

export default IndividualLoan;
